import type { EarlyDeposit } from '@/api/interfaces/early-deposit.types';
import { runAddFileSubmitCallbacks } from '@/company-portal/add-file/add-file-reset';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileRouteTypes, AddFileSubmitResponse } from '@/company-portal/add-file/add-file.types';
import { useAddFileAnalytics } from '@/company-portal/add-file/use-add-file-analytics';
import { useAddFilePayload } from '@/company-portal/add-file/use-add-file-payload';
import { useAddFileValidation } from '@/company-portal/add-file/use-add-file.validation';
import { useUserStore } from '@/stores/company/user.store';
import { computed, type MaybeRefOrGetter, ref, toValue } from 'vue';

export interface UseAddFileSubmitOptions {
    /** Route for the documents-excluded validation request. */
    validateRoute: AddFileRouteTypes['validate'];
    /** Route for the full submission request. */
    submitRoute: AddFileRouteTypes['submit'];
}

/**
 * Coordinates the Add File submission: validate (no documents) → summary confirm → similar-EMD
 * check → submit (or EMD merge). Owns the dialog open-models and clears the store's submissionErrors
 */
export const useAddFileSubmit = (submitOptions: MaybeRefOrGetter<UseAddFileSubmitOptions>) => {
    const addFileStore = useAddFileStore();
    const { trackSubmissionEvents } = useAddFileAnalytics();
    const userStore = useUserStore();
    const addFilePayload = useAddFilePayload();

    const { validateAddFileRequest } = useAddFileValidation();

    // Kept reactive so the routes can flip (e.g. to the vendor endpoints) once the TPS order loads.
    const validateOperation = addFileStore.createValidateOperation(
        computed(() => toValue(submitOptions).validateRoute),
        addFilePayload.buildValidateFormData,
    );
    const submitOperation = addFileStore.createSubmitOperation(
        computed(() => toValue(submitOptions).submitRoute),
        addFilePayload.buildSubmitFormData,
    );
    const mergeOperation = addFileStore.createMergeWithEMDOperation();
    const similarEMDSearchOperation = addFileStore.createSimilarEMDSearchOperation();

    const similarEmds = computed(() => similarEMDSearchOperation.data.value?.data ?? []);
    const fileFormData = computed(() => addFilePayload.buildSubmitFormData());
    const isSubmitting = computed(
        () =>
            validateOperation.isFetching.value ||
            submitOperation.isFetching.value ||
            mergeOperation.isFetching.value ||
            similarEMDSearchOperation.isFetching.value,
    );

    const showSummaryDialog = ref(false);
    const showSimilarEmdDialog = ref(false);

    // Close the dialogs after a submit/merge attempt regardless of outcome: on success reset the form;
    // on error keep them closed so the server's inline field errors are visible on the form.
    const finishSubmission = (response: AddFileSubmitResponse | null) => {
        showSummaryDialog.value = false;
        showSimilarEmdDialog.value = false;

        if (addFileStore.submissionErrors) {
            return;
        }

        trackSubmissionEvents(response);
        runAddFileSubmitCallbacks();
    };

    /** Step 1: validate the form (without documents); on success open the summary dialog. */
    const validateAndConfirm = async () => {
        if (isSubmitting.value) return;
        addFileStore.clearSubmissionErrors();

        // Client side validation
        const { hasError, errors: clientSideValidationErrors } = validateAddFileRequest(addFilePayload.validateRequest.value);
        if (hasError) {
            addFileStore.applySubmissionErrors(clientSideValidationErrors);
            return;
        }

        // server side validation
        await validateOperation.execute();
        if (!addFileStore.submissionErrors) {
            showSummaryDialog.value = true;
            return;
        }
    };

    /** Step 2: summary confirmed — check for similar EMDs, then open the EMD dialog or submit. */
    const confirmSummary = async () => {
        if (isSubmitting.value) return;
        if (userStore.isEmdEnabled) {
            await similarEMDSearchOperation.execute();
            if (similarEmds.value.length) {
                showSummaryDialog.value = false;
                showSimilarEmdDialog.value = true;
                return;
            }
        }
        addFileStore.clearSubmissionErrors();
        await submitOperation.execute();
        finishSubmission(submitOperation.data.value);
    };

    /** EMD dialog action: merge into the selected early deposit, or create a new file. */
    const submitFormWith = async (emdId?: EarlyDeposit['id'], formData?: FormData) => {
        addFileStore.clearSubmissionErrors();
        const { hasError, errors: clientSideValidationErrors } = validateAddFileRequest(addFilePayload.validateRequest.value);
        if (hasError) {
            addFileStore.applySubmissionErrors(clientSideValidationErrors);
            return;
        }
        if (emdId && formData) {
            addFileStore.mergeEmdId = emdId;
            addFileStore.mergeEmdFormData = formData;
            await mergeOperation.execute();
            finishSubmission(mergeOperation.data.value);
        } else {
            await submitOperation.execute();
            finishSubmission(submitOperation.data.value);
        }
    };

    return {
        isSubmitting,
        similarEmds,
        fileFormData,
        validateAndConfirm,
        confirmSummary,
        submitFormWith,
        showSummaryDialog,
        showSimilarEmdDialog,
    };
};
